import { QueryClient } from '@tanstack/react-query';

/**
 * React Query Configuration
 * Provides intelligent caching, automatic refetching, and optimized data management
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Retry failed requests once after a delay
      retry: 1,
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 30000),

      // Don't refetch on window focus by default (opt-in per query)
      refetchOnWindowFocus: false,

      // Don't refetch on reconnect by default
      refetchOnReconnect: false,

      // Refetch stale/invalidated data when component mounts
      refetchOnMount: true,
    },
    mutations: {
      // Retry mutations once on network errors
      retry: 1,
      retryDelay: 1000,
    },
  },
});

/**
 * Query keys for consistent cache management
 */
export const queryKeys = {
  // Payees / Employees
  payees: ['payees'] as const,
  payee: (id: string) => ['payees', id] as const,

  // Timesheets
  timesheets: ['timesheets'] as const,
  timesheet: (payeeId: string, weekEnding: string) =>
    ['timesheets', payeeId, weekEnding] as const,

  // Dashboard
  dashboard: ['dashboard'] as const,

  // Business / Profile
  business: ['business'] as const,
  payPeriod: ['business', 'payPeriod'] as const,

  // Taxes / 1099
  taxYears: ['taxes', 'years'] as const,
  taxYear: (year: number) => ['taxes', year] as const,

  // Reports
  reports: ['reports'] as const,
};

/**
 * Cache invalidation helpers
 */
export const invalidateQueries = {
  payees: () => queryClient.invalidateQueries({ queryKey: queryKeys.payees }),
  timesheets: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.timesheets }),
  dashboard: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard }),
  business: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.business }),
  taxes: () => queryClient.invalidateQueries({ queryKey: ['taxes'] }),
  reports: () => queryClient.invalidateQueries({ queryKey: queryKeys.reports }),
  all: () => queryClient.invalidateQueries(),
};
